import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import RequireCap from '../components/auth/RequireCap';
import { HelpButton } from '../components/HelpButton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Plus, Search, ClipboardList, Filter } from 'lucide-react';
import { formatDate, getStatusColor } from '../lib/utils';

export default function Requests() {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [showArchived]);

  const loadRequests = async () => {
    try {
      const params = showArchived ? { include_fulfilled: true } : {};
      const response = await requestsAPI.list(params);
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = requests.filter(req => {
    const matchesSearch = 
      req.request_number.toLowerCase().includes(search.toLowerCase()) ||
      req.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
      req.location_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6" data-testid="requests-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {hasRole('warehouse') ? 'Uniform Requisition Queue' : 'Uniform Requisitions'}
          </h1>
          <p className="text-slate-500 mt-1">
            {hasRole('warehouse') 
              ? 'Process and fulfill uniform requisitions'
              : 'Manage uniform requisitions for your team'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton module="requests" />
          <RequireCap module="requests" action="add">
            <Button onClick={() => navigate('/requests/new')} data-testid="new-request-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Requisition
            </Button>
          </RequireCap>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search requests..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="requests-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="picking">Picking</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="fulfilled">Fulfilled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={showArchived ? 'default' : 'outline'}
              onClick={() => { setShowArchived(!showArchived); setLoading(true); }}
              data-testid="toggle-archived-btn"
            >
              {showArchived ? 'Hide Archived' : 'Show Archived'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        {loading ? (
          <div className="p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 skeleton-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Request #</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Warehouse</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Needed By</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRequests.map(req => (
                <TableRow 
                  key={req.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/requests/${req.id}`)}
                  data-testid={`request-row-${req.id}`}
                >
                  <TableCell className="font-medium font-mono">{req.request_number}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{req.employee_name}</p>
                      <p className="text-xs text-slate-500">by {req.manager_name}</p>
                    </div>
                  </TableCell>
                  <TableCell>{req.location_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{req.lines?.length || 0} items</Badge>
                  </TableCell>
                  <TableCell>
                    {req.needed_by ? (
                      <span className={new Date(req.needed_by) < new Date() ? 'text-red-600' : ''}>
                        {formatDate(req.needed_by)}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(req.status)}>
                      {req.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(req.created_at)}</TableCell>
                </TableRow>
              ))}
              {filteredRequests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No requests found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
